// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2024 Vic Zhu <viczhu2014@gmail.com>

import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { endOfDay, format, parseISO, startOfDay } from 'date-fns'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useGetList, useStore, useTranslate } from 'react-admin'
import { trackNow } from '../analytics/usageTracker'
import {
  applyDLSBGMCorrection,
  buildGlucoseLineSegments,
  calculateAdaptiveGapThresholdMs,
  displayGlucoseToMmol,
  formatGlucoseForDisplay,
  GLUCOSE_VALID_RANGE,
  getFixedGlucoseAxis,
  glucoseSegmentTraceShape,
  isGlucoseOutOfRange,
  prepareGlucoseChartSeries,
} from '../common'
import { LazyPlot as Plot } from '../common/LazyPlot'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { useGlucoseUnitPreference } from '../hooks/useGlucoseUnitPreference'
import { petTargetRangeStoreKeys, usePetTargetRange } from '../hooks/usePetTargetRange'
import { useIsCompact } from '../hooks/useResponsive'
import { STATUS_TONES } from '../themes/statusColors'
import { Activity, FeedingEvent, InsulinEvent } from '../types'
import { GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS } from '../types/dashboard'
import { getActivityMarkerColor } from '../utils/activityConstants'
import {
  byActivityPriority,
  groupActivitiesByMinute,
  interpolateMarkerY,
  type MarkerChartPoint,
} from '../utils/activityMarkers'
import { convertGlucose, formatGlucoseValue, type GlucoseUnit } from '../utils/glucoseUnits'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { dislodgementPlotShapes } from './dislodgementOverlay'

export type ViewType = 'all' | 'day' | 'week' | 'month'

// The per-pet target-range store keys now live with the hook that layers the
// override (hooks/usePetTargetRange) so chart, detail panel and PDF export all
// resolve the range identically. Re-exported here for existing importers.
export { petTargetRangeStoreKeys }

export interface GlucoseTrendChartProps {
  deviceId?: string | number
  petId?: string | number
  enableRangeSlider?: boolean
  startDate?: Date
  endDate?: Date
  selectedDateKeys?: string[]
  defaultView?: ViewType
  onDateRangeChange?: (startDate: Date, endDate: Date) => void
  targetMin?: number
  targetMax?: number
  // Upper bound of the High zone (above this = Very High), from the pet's
  // species range — dogs 22.2, cats 27.8. MUST be the same boundary the TIR
  // capsule bins with, or the same reading is red here and orange there
  // (eng-brain P13-1). Falls back to the flat GLUCOSE_THRESHOLDS.VERY_HIGH
  // when a parent doesn't know the species.
  highMax?: number
  // Reports whether the chart has finished loading and has data to show — i.e.
  // its settings dialog is mounted and openSettings() will actually show it.
  // The parent uses this to disable the "Configure Target Range" button until
  // the chart is ready (otherwise a click during load would be inert).
  onReadyChange?: (ready: boolean) => void
}

// Cap color, segment builder, and the adaptive gap-threshold calc now live in
// ../common (single source of truth shared by every glucose line chart).
// Re-exported here for back-compat: existing imports and tests reference them
// from this module.
export {
  buildGlucoseLineSegments,
  calculateAdaptiveGapThresholdMs,
  GLUCOSE_CAP_COLOR,
  type GlucoseLineSegment,
} from '../common'

// Imperative handle so an ancestor (e.g. the "Configure Target Range" button
// rendered next to the "Glucose Trend" heading) can open the same target-range
// settings dialog the in-chart gear opens.
/**
 * One tooltip line for a plotted glucose point.
 *
 * The chart plots a 5-point trailing moving average (smoothCGM) of
 * BGM-corrected readings, while the mobile app's History tab and the CSV
 * exports show the per-reading corrected value — so during a fast rise or fall
 * the two legitimately disagree by up to ~10 mg/dL (support incident
 * 2026-07-29: chart 112 vs History 103 while glucose fell 119→100 in 15 min).
 * When the average and the underlying reading differ after display rounding,
 * show both ("avg 112 · reading 103 mg/dL") so the gap explains itself;
 * when they agree, keep the plain single value.
 */
export const formatGlucoseHoverPoint = (
  timeStr: string,
  smoothedMmol: number,
  actualMmol: number | null | undefined,
  unit: GlucoseUnit,
  translate: (key: string) => string,
): string => {
  if (isGlucoseOutOfRange(smoothedMmol)) {
    return `${timeStr} | ${translate('pos.glucose.out_of_range')} (${formatGlucoseForDisplay(smoothedMmol, unit)})`
  }
  const plain = `${timeStr} | ${formatGlucoseForDisplay(smoothedMmol, unit)}`
  if (actualMmol === null || actualMmol === undefined) return plain
  const avgValue = formatGlucoseValue(smoothedMmol, unit)
  // An out-of-range reading is SHOWN, masked ("<40" / ">540"), not dropped:
  // hiding it behind the smoothed average is how a sensor drop-out used to
  // disappear from the tooltip entirely. formatGlucoseForDisplay does the
  // masking, but only if it receives the PRE-clamp corrected value — the clamp
  // floors to exactly the mask's threshold (see formatGlucoseForExport).
  const outOfRange = isGlucoseOutOfRange(actualMmol)
  if (!outOfRange && avgValue === formatGlucoseValue(actualMmol, unit)) return plain
  return `${timeStr} | ${translate('pos.glucose.tooltip_avg')} ${avgValue} · ${translate('pos.glucose.tooltip_reading')} ${formatGlucoseForDisplay(actualMmol, unit)}`
}

export interface GlucoseTrendChartHandle {
  openSettings: () => void
}

// ─── Time-range computation ──────────────────────────────────────────────────
// Pure module-level functions (extracted from the component body 2026-08-16,
// vetpo#1 — it had reached CCN 22 / cognitive 31 / nesting 5 inline). Inputs
// are explicit so the initialRange useMemo's dependency list in the component
// is the complete truth about what can change the range.

const DAY_MS = 24 * 60 * 60 * 1000

interface TimeRangeInputs {
  viewType: ViewType
  selectedDateKeys: string[]
  propStartDate?: Date
  propEndDate?: Date
  dateRangeMode: boolean
  rangeStartDate: Date | null
  rangeEndDate: Date | null
  selectedDate: Date | null
  metricsData: Array<{ timestamp: number }>
}

/** Full-day range across [a, b], swapping endpoints picked in reverse order. */
const orderedDayRange = (a: Date, b: Date): { start: Date; end: Date } => {
  const start = startOfDay(a)
  const end = endOfDay(b)
  if (start <= end) return { start, end }
  return { start: startOfDay(b), end: endOfDay(a) }
}

/** Most recent reading's date, or null (metricsData is sorted timestamp ASC). */
const lastMetricDate = (metricsData: Array<{ timestamp: number }>): Date | null => {
  const last = metricsData[metricsData.length - 1]
  return last?.timestamp ? new Date(last.timestamp * 1000) : null
}

/**
 * Default reference date: explicit range start → user-picked date → the most
 * recent reading (defaulting to "today" showed an empty chart whenever the
 * device's latest data was from yesterday or earlier) → now.
 */
const getDefaultReferenceDate = (i: TimeRangeInputs): Date => {
  if (i.dateRangeMode && i.rangeStartDate) return i.rangeStartDate
  if (i.selectedDate) return i.selectedDate
  return lastMetricDate(i.metricsData) ?? new Date()
}

/** Date-range mode with only a start picked: anchor it on the left, extend by view. */
const rangeAnchoredAtStart = (
  rangeStartDate: Date,
  viewType: ViewType,
  metricsData: Array<{ timestamp: number }>,
): { start: Date; end: Date } => {
  const start = startOfDay(rangeStartDate)
  switch (viewType) {
    case 'all': {
      const mostRecent = lastMetricDate(metricsData)
      const end = mostRecent ? endOfDay(mostRecent) : null
      return { start, end: end && end > start ? end : endOfDay(rangeStartDate) }
    }
    case 'day':
      return { start, end: endOfDay(rangeStartDate) }
    case 'week':
      return { start, end: endOfDay(new Date(rangeStartDate.getTime() + 6 * DAY_MS)) }
    case 'month':
      return { start, end: endOfDay(new Date(rangeStartDate.getTime() + 29 * DAY_MS)) }
  }
}

/**
 * Range for a view type, rolling back from the reference date (inclusive):
 * Day = the reference day only; Week = 7 days; Month = 30 days; All = first
 * reading through last reading.
 */
const rangeForView = (
  viewType: ViewType,
  referenceDate: Date,
  metricsData: Array<{ timestamp: number }>,
): { start: Date; end: Date } => {
  switch (viewType) {
    case 'all': {
      const first = metricsData[0]
      const last = metricsData[metricsData.length - 1]
      if (first && last) {
        return {
          start: startOfDay(new Date(first.timestamp * 1000)),
          end: endOfDay(new Date(last.timestamp * 1000)),
        }
      }
      return { start: startOfDay(referenceDate), end: endOfDay(referenceDate) }
    }
    case 'day':
      return { start: startOfDay(referenceDate), end: endOfDay(referenceDate) }
    case 'week':
      return {
        start: startOfDay(new Date(referenceDate.getTime() - 6 * DAY_MS)),
        end: endOfDay(referenceDate),
      }
    case 'month':
      return {
        start: startOfDay(new Date(referenceDate.getTime() - 29 * DAY_MS)),
        end: endOfDay(referenceDate),
      }
  }
}

/**
 * Initial time range for the chart, in precedence order:
 * 1. Externally linked Daily-TIR selection from the Dashboard (even when
 *    dateRangeMode was never toggled inside this component);
 * 2. Explicit start+end range (guarding inverted selection order);
 * 3. Start-only range, anchored left and extended by view type;
 * 4. The view type rolled back from the reference date.
 */
const getInitialTimeRange = (i: TimeRangeInputs): { start: Date; end: Date } => {
  if (i.selectedDateKeys.length > 0 && i.propStartDate && i.propEndDate) {
    return orderedDayRange(i.propStartDate, i.propEndDate)
  }
  if (i.dateRangeMode && i.rangeStartDate && i.rangeEndDate) {
    return orderedDayRange(i.rangeStartDate, i.rangeEndDate)
  }
  if (i.dateRangeMode && i.rangeStartDate) {
    return rangeAnchoredAtStart(i.rangeStartDate, i.viewType, i.metricsData)
  }
  return rangeForView(i.viewType, getDefaultReferenceDate(i), i.metricsData)
}

// ─── Plotly shape/annotation builders ────────────────────────────────────────
// Pure config assembly, extracted from the component body (vetpo#1). All
// thresholds arrive already converted to the active display unit.

/** Hover annotation naming the configured target band. */
const buildTargetAnnotation = (opts: {
  lowThreshold: number
  highThreshold: number
  targetLabel: string
}): any => ({
  x: 0.02,
  y: (opts.lowThreshold + opts.highThreshold) / 2,
  xref: 'paper',
  yref: 'y',
  text: opts.targetLabel,
  showarrow: false,
  font: {
    size: 11,
    color: '#4caf50',
    family: 'Roboto, sans-serif',
  },
  bgcolor: 'rgba(255, 255, 255, 0.9)',
  borderpad: 4,
  bordercolor: '#4caf50',
  borderwidth: 1,
  opacity: 0.9,
})

/** Light-gray dashed vertical line at each midnight where the plotted dates change. */
const buildDateSeparatorShapes = (metricsX: Date[]): any[] => {
  const dateChanges: Date[] = []
  for (let i = 1; i < metricsX.length; i++) {
    const currentDate = new Date(metricsX[i])
    const prevDate = new Date(metricsX[i - 1])
    if (currentDate.toDateString() !== prevDate.toDateString()) {
      dateChanges.push(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()),
      )
    }
  }
  return dateChanges.map((separatorTime) => ({
    type: 'line',
    x0: separatorTime,
    x1: separatorTime,
    y0: 0,
    y1: 1,
    xref: 'x',
    yref: 'paper',
    line: {
      color: '#ddd',
      width: 1,
      dash: 'dash',
    },
    layer: 'below',
  }))
}

interface GlucoseZoneOptions {
  /** Zone boundaries, display unit. veryHigh is the pet's SPECIES cut (see the caller). */
  veryLowThreshold: number
  lowThreshold: number
  highThreshold: number
  veryHighThreshold: number
  yAxisMax: number
  /** Danger overlays: [2.2, 3.0] and [30, 40] mmol/L in the display unit. */
  dangerLowBottom: number
  dangerLowTop: number
  dangerHighBottom: number
  dangerHighTop: number
  /** Label for the target rect, or null when the band is not configured. */
  targetLabel: string | null
}

/** Full-width horizontal rect spanning [y0, y1], tinted `color` at hex-suffix `alpha`. */
const zoneRect = (y0: number, y1: number, color: string, alpha: string): any => ({
  type: 'rect',
  x0: 0,
  x1: 1,
  y0,
  y1,
  xref: 'paper',
  yref: 'y',
  fillcolor: `${color}${alpha}`,
  line: { width: 0 },
  layer: 'below',
})

/**
 * Background zone rects (Very Low → Very High), the dashed target boundary
 * lines, and the stronger danger-band overlays at the sensor limits.
 */
const buildGlucoseZoneShapes = (z: GlucoseZoneOptions): any[] => {
  const shapes: any[] = []

  // Very Low (purple) and Low (blue) washes
  shapes.push(zoneRect(0, z.veryLowThreshold, TIR_ZONE_COLORS.veryLow, '15'))
  shapes.push(zoneRect(z.veryLowThreshold, z.lowThreshold, TIR_ZONE_COLORS.low, '15'))

  // Target zone (green, configurable) + dashed boundary lines
  if (z.targetLabel !== null) {
    shapes.push({
      ...zoneRect(z.lowThreshold, z.highThreshold, TIR_ZONE_COLORS.target, '20'),
      name: z.targetLabel,
    })
    for (const y of [z.lowThreshold, z.highThreshold]) {
      shapes.push({
        type: 'line',
        x0: 0,
        x1: 1,
        y0: y,
        y1: y,
        yref: 'y',
        xref: 'paper',
        line: { color: TIR_ZONE_COLORS.target, width: 2, dash: 'dash' },
      })
    }
  }

  // High (orange) and Very High (red) washes
  shapes.push(zoneRect(z.highThreshold, z.veryHighThreshold, TIR_ZONE_COLORS.high, '15'))
  shapes.push(zoneRect(z.veryHighThreshold, z.yAxisMax, TIR_ZONE_COLORS.veryHigh, '15'))

  // Danger bands: stronger wash + dotted border at the sensor floor/ceiling
  shapes.push(zoneRect(z.dangerLowBottom, z.dangerLowTop, TIR_ZONE_COLORS.veryLow, '30'), {
    type: 'line',
    x0: 0,
    x1: 1,
    y0: z.dangerLowTop,
    y1: z.dangerLowTop,
    xref: 'paper',
    yref: 'y',
    line: { color: TIR_ZONE_COLORS.veryLow, width: 1, dash: 'dot' },
    layer: 'below',
  })
  shapes.push(zoneRect(z.dangerHighBottom, z.dangerHighTop, TIR_ZONE_COLORS.veryHigh, '30'), {
    type: 'line',
    x0: 0,
    x1: 1,
    y0: z.dangerHighBottom,
    y1: z.dangerHighBottom,
    xref: 'paper',
    yref: 'y',
    line: { color: TIR_ZONE_COLORS.veryHigh, width: 1, dash: 'dot' },
    layer: 'below',
  })

  return shapes
}

// ─── Activity marker building ────────────────────────────────────────────────
// Pure helpers for the insulin/food/exercise overlay markers (extracted from
// the component body 2026-08-16, vetpo#1 — the group-loop had reached CCN 39 /
// cognitive 62 inline). Grouping, curve interpolation and priority live in
// utils/activityMarkers — shared with the vet PDF report (vic/vetpo#46), which
// must place markers by the same rules.
//
// NOTE: the brief-description JSON parsing here is a THIRD sibling of
// utils/activityUtils.formatActivityDescription and ActivityDatagrid's private
// copy (see docs/QUALITY-BASELINE.md, duplication finding 2). Not converged in
// this refactor — hover text is deliberately terser than the datagrid's prose —
// but a format change to the insulin/food payloads must touch all three.

type ChartPoint = MarkerChartPoint

/**
 * Structural subset of Activity the marker pipeline actually reads. Lets the
 * structured meal/insulin markers built below (vic/vetpo#15) reuse the exact
 * same grouping/color/priority pipeline as legacy `activities` without
 * carrying Activity's unrelated required fields (owner, id, timestamps).
 */
type MarkerSource = Pick<Activity, 'type' | 'start_time' | 'description'>

/** Brief hover description for one activity, parsing the insulin/food JSON payloads. */
const describeActivityBrief = (activity: MarkerSource): string => {
  try {
    const parsed = JSON.parse(activity.description || '{}')
    if (activity.type === 'insulin') {
      const { brand, customBrand, dosage } = parsed
      const parts = []
      if (brand && brand !== 'custom') parts.push(brand)
      if (customBrand) parts.push(customBrand)
      if (dosage) parts.push(`${dosage} units`)
      return parts.join(' — ')
    }
    if (activity.type === 'food' || activity.type === 'feeding') {
      const { foodType, amount, unit } = parsed
      const parts = []
      if (foodType) parts.push(foodType)
      if (amount && unit) parts.push(`${amount} ${unit}`)
      else if (amount) parts.push(amount)
      return parts.join(' — ')
    }
    return activity.description || ''
  } catch {
    return activity.description || ''
  }
}

/** One "  - type: detail" line for an activity inside a concurrent group's hover. */
const concurrentActivityLine = (activity: MarkerSource): string => {
  let line = `  - ${activity.type || 'Activity'}`
  if (activity.description) {
    try {
      const parsed = JSON.parse(activity.description)
      if (activity.type === 'insulin' && parsed.dosage) {
        line += `: ${parsed.dosage} units`
      } else if ((activity.type === 'food' || activity.type === 'feeding') && parsed.foodType) {
        line += `: ${parsed.foodType}`
      }
    } catch {
      // If not JSON, show first 30 chars
      line += `: ${activity.description.substring(0, 30)}`
    }
  }
  return line
}

/** Hover text for a marker: single activity → one line; concurrent → a list. */
const buildActivityMarkerHoverText = (opts: {
  activities: MarkerSource[]
  sortedActivities: MarkerSource[]
  date: Date
  isEstimated: boolean
  translate: (key: string) => string
}): string => {
  const { activities, sortedActivities, date, isEstimated, translate } = opts
  const timeStr = format(date, 'HH:mm')
  if (activities.length === 1) {
    const activity = activities[0]
    const typeStr = activity.type || 'Activity'
    const description = describeActivityBrief(activity)
    return `${timeStr} • ${typeStr}${description ? ` • ${description}` : ''}${isEstimated ? ` ${translate('pos.glucose.estimated')}` : ''}`
  }
  let hoverText = `${timeStr} • ${activities.length} activities:<br>`
  hoverText += sortedActivities.map(concurrentActivityLine).join('<br>')
  if (isEstimated) {
    hoverText += `<br>  ${translate('pos.glucose.y_position_estimated')}`
  }
  return hoverText
}

/**
 * Single scatter trace holding every activity marker (one trace keeps
 * rendering consistent). Concurrent same-minute activities share a marker:
 * highest-priority activity's color, gray border. Returns null when there is
 * nothing to draw.
 */
const buildActivityMarkersTrace = (opts: {
  filteredEvents: MarkerSource[]
  processedData: ChartPoint[]
  defaultY: number
  translate: (key: string) => string
}): any | null => {
  const { filteredEvents, processedData, defaultY, translate } = opts
  const markerData = {
    x: [] as Date[],
    y: [] as number[],
    text: [] as string[],
    colors: [] as string[],
    borderColors: [] as string[],
  }

  groupActivitiesByMinute(filteredEvents).forEach((activities, timestamp) => {
    const date = new Date(timestamp)
    const isConcurrent = activities.length >= 2
    const { markerY, isEstimated } = interpolateMarkerY(processedData, timestamp, defaultY)

    const sortedActivities = [...activities].sort(byActivityPriority)
    const primaryActivity = sortedActivities[0]
    const activityType = primaryActivity.type?.toLowerCase() || 'default'

    markerData.x.push(date)
    markerData.y.push(markerY)
    markerData.colors.push(getActivityMarkerColor(activityType))
    // Gray border for concurrent, white for single
    markerData.borderColors.push(isConcurrent ? '#808080' : 'white')
    markerData.text.push(
      buildActivityMarkerHoverText({ activities, sortedActivities, date, isEstimated, translate }),
    )
  })

  if (markerData.x.length === 0) return null
  return {
    x: markerData.x,
    y: markerData.y,
    type: 'scatter',
    mode: 'markers',
    marker: {
      color: markerData.colors, // Per-marker colors
      size: 7, // Default 7px diameter (6-8px range)
      sizemode: 'diameter', // Use pixel size, not scaled by data values
      symbol: 'circle',
      line: {
        color: markerData.borderColors, // Per-marker border colors
        width: 2,
      },
    },
    showlegend: false,
    hovertemplate: '%{text}<extra></extra>',
    text: markerData.text,
    hoverinfo: 'text',
    hoverlabel: {
      bgcolor: '#ffffff',
      bordercolor: '#cccccc',
      font: { size: 12, color: '#333333', family: 'system-ui, -apple-system, sans-serif' },
    },
  }
}

// ─── Structured meal/insulin marker adapters (vic/vetpo#15) ─────────────────
// FeedingEvents.outcome and InsulinEvents replace the legacy activities-text
// source for meal-status and insulin markers going forward. Rather than a
// second overlay, these adapt each structured row into a MarkerSource and
// feed it into the SAME groupActivitiesByMinute / buildActivityMarkersTrace
// pipeline above, so same-minute grouping and priority ordering stay in one
// place. Descriptions are pre-formatted plain text (never JSON), so
// describeActivityBrief's JSON.parse fails fast and falls through to the
// plain-text branch untouched.

const INSULIN_TYPE_LABELS: Record<string, string> = {
  vetsulin: 'Vetsulin',
  prozinc: 'ProZinc',
  glargine: 'Glargine (Lantus)',
  detemir: 'Detemir (Levemir)',
  nph: 'NPH',
  regular: 'Regular',
  other: 'Other',
}

const INSULIN_LATE_THRESHOLD_MS = 30 * 60 * 1000

/** Epoch seconds for a Payload ISO date string, or null if missing/invalid. */
const toEpochSeconds = (iso: string | null | undefined): number | null => {
  if (!iso) return null
  const ms = new Date(iso).getTime()
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000)
}

/**
 * Meal-status markers from FeedingEvents with a logged outcome. A row with no
 * outcome is an unanswered meal, not "ate normally" — it gets no marker (see
 * FeedingEvents.outcome's own validateMealOutcome contract).
 */
export const feedingEventsToMarkers = (
  events: FeedingEvent[],
  translate: (key: string) => string,
): MarkerSource[] =>
  events.reduce<MarkerSource[]>((markers, event) => {
    const startTime = toEpochSeconds(event.actualTime)
    if (!event.outcome || startTime === null) return markers
    const labelKey =
      event.outcome === 'normal'
        ? 'pos.glucose.meal_status_normal'
        : event.outcome === 'less'
          ? 'pos.glucose.meal_status_less'
          : 'pos.glucose.meal_status_none'
    markers.push({
      type: 'meal',
      start_time: startTime,
      description: `🍽 ${translate(labelKey)}`,
    })
    return markers
  }, [])

/**
 * The InsulinEvents.guidance values that mean an exception instruction was
 * shown INSTEAD of the default dose. ada-core#193 also stores 'default' — the
 * routine dose prompt — which is NOT an exception (eng-brain P69-2: a
 * truthiness check drew the warning on every ordinary dose).
 */
const INSULIN_EXCEPTION_GUIDANCE: ReadonlySet<string> = new Set([
  'eating_less',
  'not_eating',
  'contact_clinic',
])

/**
 * Dose/skipped markers from the structured InsulinEvents log, plus a
 * separate, visually distinct marker when guidance names an exception
 * instruction (see INSULIN_EXCEPTION_GUIDANCE) fired instead of the default
 * dose. `guidance` is read ONLY from InsulinEvents.guidance, never derived
 * from the preceding meal outcome (that would assert what the owner was
 * shown, which also depends on plan text and lookup success). NULL / absent
 * means no guidance was recorded — no exception marker.
 */
export const insulinEventsToMarkers = (
  events: InsulinEvent[],
  translate: (key: string) => string,
): MarkerSource[] =>
  events.reduce<MarkerSource[]>((markers, event) => {
    // ada-core#298: the owner voided this event as mistyped/incorrect within
    // 24h of logging it — it must not render as a dose the vet would act on.
    if (event.voidedAt) return markers
    const startTime = toEpochSeconds(event.actualTime)
    if (startTime === null) return markers

    const scheduledMs = event.scheduledTime ? new Date(event.scheduledTime).getTime() : NaN
    const actualMs = new Date(event.actualTime).getTime()
    const isLate = !Number.isNaN(scheduledMs) && actualMs - scheduledMs > INSULIN_LATE_THRESHOLD_MS
    const insulinLabel = INSULIN_TYPE_LABELS[event.insulinType] || event.insulinType
    const description =
      event.eventType === 'skipped'
        ? translate('pos.glucose.insulin_dose_skipped')
        : `${event.units} ${translate('pos.glucose.insulin_units_abbr')} — ${insulinLabel}${
            isLate ? ` ${translate('pos.glucose.insulin_dose_late_suffix')}` : ''
          }`
    markers.push({ type: 'insulin', start_time: startTime, description })

    if (event.guidance && INSULIN_EXCEPTION_GUIDANCE.has(event.guidance)) {
      markers.push({
        type: 'exception',
        start_time: startTime,
        description: `⚠️ ${translate('pos.glucose.insulin_exception_triggered')}`,
      })
    }
    return markers
  }, [])

// ─── Plot identity & layout ──────────────────────────────────────────────────

/**
 * Remount key + uirevision for the Plot. Same recipe, ONE place: they used to
 * be two near-identical inline template strings whose only difference is that
 * the remount key also changes with the display unit (a unit switch must
 * re-render axes; it must not count as a user zoom for uirevision purposes).
 */
const buildPlotRevisionKeys = (opts: {
  deviceId: string | number | undefined
  viewType: ViewType
  selectedDate: Date | null
  rangeStartDate: Date | null
  rangeEndDate: Date | null
  glucoseUnit: string
  chartResetVersion: number
}): { plotKey: string; uirevision: string } => {
  const base = `${opts.deviceId}-${opts.viewType}-${opts.selectedDate?.toISOString() || 'now'}-${opts.rangeStartDate?.toISOString() || 'none'}-${opts.rangeEndDate?.toISOString() || 'none'}`
  return {
    plotKey: `${base}-${opts.glucoseUnit}-${opts.chartResetVersion}`,
    uirevision: `${base}-${opts.chartResetVersion}`,
  }
}

/** The quick-preset buttons, in display order. */
const VIEW_PRESETS = [
  { type: 'day', labelKey: 'pos.glucose.view_day', fallback: 'LAST DAY' },
  { type: 'week', labelKey: 'pos.glucose.view_week', fallback: 'LAST WEEK' },
  { type: 'month', labelKey: 'pos.glucose.view_month', fallback: 'LAST MONTH' },
  { type: 'all', labelKey: 'pos.glucose.view_all', fallback: 'ALL' },
] as const satisfies ReadonlyArray<{ type: ViewType; labelKey: string; fallback: string }>

/** Plotly layout for the trend chart — pure config assembly. */
const buildTrendChartLayout = (opts: {
  translate: (key: string) => string
  glucoseUnit: string
  effectiveRange: { start: Date; end: Date }
  viewType: ViewType
  enableRangeSlider: boolean
  yAxisMin: number
  yAxisMax: number
  fixedAxis: { tickValues: number[]; tickLabels: string[] }
  shapes: any[]
  annotations: any[]
  uirevision: string
}): any => ({
  title: '',
  xaxis: {
    title: opts.translate('resources.glucose.axis_labels.date'),
    type: 'date',
    rangeslider: { visible: opts.enableRangeSlider },
    range: [opts.effectiveRange.start, opts.effectiveRange.end],
    tickformat: opts.viewType === 'day' ? '%H:%M' : '%b %d',
    tickmode: 'auto',
    gridcolor: 'transparent',
    zerolinecolor: '#e0e0e0',
    showspikes: true,
    spikemode: 'across',
    spikethickness: 1,
    spikecolor: 'rgba(0, 0, 0, 0.3)',
    spikedash: 'dot',
    spikesnap: 'cursor',
    autorange: false,
  },
  yaxis: {
    title: `${opts.translate('resources.glucose.axis_labels.glucose')} (${opts.glucoseUnit})`,
    range: [opts.yAxisMin, opts.yAxisMax],
    // Y-axis ticks: fixed values (see getFixedGlucoseAxis) so the scale is
    // consistent and all high glucose values are visible without clipping
    tickmode: 'array',
    tickvals: opts.fixedAxis.tickValues,
    ticktext: opts.fixedAxis.tickLabels,
    gridcolor: '#e0e0e0',
    zerolinecolor: '#e0e0e0',
    showspikes: true,
    spikemode: 'across',
    spikethickness: 1,
    spikecolor: 'rgba(0, 0, 0, 0.3)',
    spikedash: 'dot',
    spikesnap: 'cursor',
    autorange: false,
  },
  uirevision: opts.uirevision,
  margin: { t: 10, l: 40, r: 10, b: 60 },
  shapes: opts.shapes,
  annotations: opts.annotations,
  plot_bgcolor: '#ffffff',
  paper_bgcolor: '#ffffff',
  font: {
    family: 'Roboto, sans-serif',
    size: 12,
    color: 'rgba(0, 0, 0, 0.87)',
  },
  height: 300,
  hovermode: 'x unified',
  hoverlabel: {
    bgcolor: '#000000',
    bordercolor: '#000000',
    font: {
      size: 14,
      color: '#FFFFFF',
      family: 'system-ui, -apple-system, sans-serif',
      weight: 600,
    },
    align: 'left',
    namelength: 0,
  },
  hoverdistance: 5,
  dragmode: 'pan',
  transition: {
    duration: 200,
    easing: 'cubic-in-out',
  },
})

/**
 * Everything drawn behind the glucose line: sensor-detachment bands (so a
 * filtered-out gap reads as an intentional safety hold, not missing data),
 * the target-band hover annotation, day-separator lines, and the zone washes.
 */
const buildChartDecorations = (opts: {
  hiddenRanges: Parameters<typeof dislodgementPlotShapes>[0]
  translate: (key: string) => string
  metricsX: Date[]
  targetRangeLow: number | undefined
  targetRangeHigh: number | undefined
  lowThreshold: number
  highThreshold: number
  /** Species Very-High boundary (dogs 22.2 / cats 27.8), mmol/L, if known. */
  highMax: number | undefined
  yAxisMax: number
  glucoseUnit: string
  convertValue: (mmolValue: number) => number
  formatValue: (mmolValue: number) => string
}): { shapes: any[]; annotations: any[] } => {
  const shapes: any[] = []
  const annotations: any[] = []

  const detachmentOverlay = dislodgementPlotShapes(opts.hiddenRanges, opts.translate)
  shapes.push(...detachmentOverlay.shapes)
  annotations.push(...detachmentOverlay.annotations)

  const targetValuesLabel =
    typeof opts.targetRangeLow === 'number' && typeof opts.targetRangeHigh === 'number'
      ? `${opts.formatValue(opts.targetRangeLow)} - ${opts.formatValue(opts.targetRangeHigh)} ${opts.glucoseUnit}`
      : null

  // Target-range annotation (shows on hover)
  if (targetValuesLabel !== null) {
    annotations.push(
      buildTargetAnnotation({
        lowThreshold: opts.lowThreshold,
        highThreshold: opts.highThreshold,
        targetLabel: `Target: ${targetValuesLabel}`,
      }),
    )
  }

  shapes.push(...buildDateSeparatorShapes(opts.metricsX))

  // Very High starts at the pet's species boundary (dogs 22.2 / cats 27.8) —
  // the SAME cut calculateTIRStats bins with. Reading the flat 13.9 here made
  // this chart paint readings red that the TIR capsule above it counted as
  // High (29.3% of prod dog readings; eng-brain P13-1 / L-021).
  shapes.push(
    ...buildGlucoseZoneShapes({
      veryLowThreshold: opts.convertValue(GLUCOSE_THRESHOLDS.VERY_LOW),
      lowThreshold: opts.lowThreshold,
      highThreshold: opts.highThreshold,
      veryHighThreshold: opts.convertValue(opts.highMax ?? GLUCOSE_THRESHOLDS.VERY_HIGH),
      yAxisMax: opts.yAxisMax,
      dangerLowBottom: opts.convertValue(GLUCOSE_VALID_RANGE.min), // 2.2
      dangerLowTop: opts.convertValue(3.0),
      dangerHighBottom: opts.convertValue(30),
      dangerHighTop: opts.convertValue(GLUCOSE_VALID_RANGE.max), // 40
      targetLabel: targetValuesLabel === null ? null : `Target Range: ${targetValuesLabel}`,
    }),
  )

  return { shapes, annotations }
}

/**
 * Metrics whose timestamps fall inside [viewStart, viewEnd], optionally
 * narrowed to the day keys of a linked Daily-TIR selection.
 * CRITICAL: filters by BOTH start AND end time — filtering only by start once
 * made Day view show multiple days.
 */
const filterMetricsToWindow = <T extends { timestamp: number }>(
  metricsData: T[],
  viewStartTime: Date,
  viewEndTime: Date,
  selectedDateKeys: string[],
): T[] => {
  let windowed = metricsData.filter((metric) => {
    const metricDate = new Date(metric.timestamp * 1000)
    return metricDate >= viewStartTime && metricDate <= viewEndTime
  })
  if (selectedDateKeys.length > 0) {
    const selectedDateSet = new Set(selectedDateKeys)
    windowed = windowed.filter((metric) =>
      selectedDateSet.has(format(new Date(metric.timestamp * 1000), 'yyyy-MM-dd')),
    )
  }
  return windowed
}

/**
 * The colored glucose line, as one scatter trace per same-color segment.
 *
 * Hover text masks out-of-range readings (high >30 mmol/L / 540 mg/dL, low
 * <2.2 / 40) as "Out of range" instead of a meaningless number. rawY arrives
 * in the display unit, so it is converted back to mmol/L (toMmol) for the
 * single-source formatter (formatGlucoseForDisplay / isGlucoseOutOfRange),
 * keeping every chart, the AGP, and the Daily Trend stats in agreement.
 *
 * The per-reading map is built from the RAW rows via applyDLSBGMCorrection,
 * NOT from the normalized series — normalizeGlucoseReadingForChart CLAMPS, the
 * clamp floors to exactly GLUCOSE_OUT_OF_RANGE_LOW, and the mask tests with a
 * strict `<`, so a clamped drop-out could never be masked and the tooltip read
 * "reading 40 mg/dL" for a reading the sensor never made. Identical for every
 * in-band value (the clamp is identity there).
 */
export const buildGlucoseLineTraces = (opts: {
  metricsX: Date[]
  metricsY: number[]
  /** Raw (uncorrected) rows for the window — feeds the tooltip's per-reading value. */
  rawWindowRows: Array<{ timestamp?: number; glucose: number }>
  lowThreshold: number
  highThreshold: number
  displayFloor: number
  displayCeil: number
  glucoseUnit: GlucoseUnit
  toMmol: (displayValue: number) => number
  translate: (key: string) => string
}): any[] => {
  // Corrected-but-UNsmoothed values, keyed by reading timestamp, so the
  // tooltip can show the underlying reading next to the plotted average.
  const actualMmolByTs = new Map(
    opts.rawWindowRows
      .filter((m) => typeof m.glucose === 'number' && m.timestamp !== undefined)
      .map((m) => [m.timestamp, applyDLSBGMCorrection(m.glucose)]),
  )
  const buildHoverText = (xs: Date[], ys: (number | null)[]) =>
    xs.map((d, i) => {
      const timeStr = format(d, 'hh:mm a')
      const v = ys[i]
      if (v === null || v === undefined) return timeStr
      const actual = actualMmolByTs.get(Math.round(d.getTime() / 1000)) ?? null
      return formatGlucoseHoverPoint(
        timeStr,
        opts.toMmol(v),
        actual,
        opts.glucoseUnit,
        opts.translate,
      )
    })

  // Handle gaps for invalid readings (null values) and time gaps between
  // devices, with an adaptive threshold so gaps still render when the sampling
  // interval changes (e.g. zoomed/aggregated windows with >3-minute spacing).
  const segments = buildGlucoseLineSegments(opts.metricsX, opts.metricsY, {
    lowThreshold: opts.lowThreshold,
    highThreshold: opts.highThreshold,
    displayFloor: opts.displayFloor,
    displayCeil: opts.displayCeil,
    gapThresholdMs: calculateAdaptiveGapThresholdMs(opts.metricsX),
  })
  return segments.map((seg) => {
    // A one-point 'lines' trace draws NOTHING in Plotly. buildGlucoseLineSegments
    // carries a color-transition point into the segment it leaves and starts the
    // next one with that same point, so whenever the LAST reading changes band the
    // new segment is a singleton — and the newest reading, the one a vet reads
    // first, silently vanished while the visible line ended in the previous band's
    // color (pet A0004BV, 2026-08-26: sensor detached off the top of the axis, the
    // final on-scale point was invisible and the line ended grey). A lone reading
    // between two time gaps disappeared the same way. Draw a marker for those, and
    // only those — every segment with >= 2 points renders exactly as before.
    return {
      x: seg.x,
      y: seg.y,
      type: 'scatter',
      ...glucoseSegmentTraceShape(seg.x.length, seg.color),
      line: { color: seg.color, width: 2 },
      showlegend: false,
      text: buildHoverText(seg.x, seg.rawY),
      hovertemplate: '%{text}<extra></extra>',
      hoverinfo: 'text',
    }
  })
}

/**
 * True when any chart-local filter state differs from what the props (or their
 * defaults) dictate — the "Reset view" button lights up on this.
 */
const hasLocalFilterOverrides = (o: {
  viewType: ViewType
  defaultView: ViewType
  dateRangeMode: boolean
  initialDateRangeMode: boolean
  selectedDate: Date | null
  rangeStartDate: Date | null
  rangeEndDate: Date | null
  propStartTimestamp: number | null
  propEndTimestamp: number | null
}): boolean =>
  o.viewType !== o.defaultView ||
  o.dateRangeMode !== o.initialDateRangeMode ||
  o.selectedDate !== null ||
  (o.rangeStartDate?.getTime() ?? null) !== o.propStartTimestamp ||
  (o.rangeEndDate?.getTime() ?? null) !== o.propEndTimestamp

/**
 * The date-selection cluster: single-date or start/end range pickers, the
 * mode toggle between them, and Clear. Owns none of the state — the parent
 * chart does, because the pickers drive the plotted range.
 */
const DateSelectionControls = ({
  dateRangeMode,
  setDateRangeMode,
  selectedDate,
  setSelectedDate,
  rangeStartDate,
  setRangeStartDate,
  rangeEndDate,
  setRangeEndDate,
  onDateRangeChange,
  firstMetricTimestamp,
}: {
  dateRangeMode: boolean
  setDateRangeMode: (v: boolean) => void
  selectedDate: Date | null
  setSelectedDate: (v: Date | null) => void
  rangeStartDate: Date | null
  setRangeStartDate: (v: Date | null) => void
  rangeEndDate: Date | null
  setRangeEndDate: (v: Date | null) => void
  onDateRangeChange?: (startDate: Date, endDate: Date) => void
  /** First reading's timestamp (seconds), for the single-date picker placeholder. */
  firstMetricTimestamp?: number
}) => {
  const translate = useTranslate()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {dateRangeMode ? (
          // Date Range Mode: Start Date and End Date
          <>
            <DatePicker
              label={translate('pos.glucose.start_date')}
              value={rangeStartDate}
              onChange={(newDate) => {
                // Ignore mid-edit Invalid Date objects (MUI emits one
                // while a field is being typed) so we never store NaN.
                if (newDate && Number.isNaN(newDate.getTime())) return
                setRangeStartDate(newDate)
                // Notify parent if both dates are set
                if (newDate && rangeEndDate && onDateRangeChange) {
                  onDateRangeChange(newDate, rangeEndDate)
                }
                // Auto-switch back to single date mode if cleared
                if (!newDate && !rangeEndDate) {
                  setDateRangeMode(false)
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: 180 } },
                },
              }}
            />
            <DatePicker
              label={translate('pos.glucose.end_date')}
              value={rangeEndDate}
              onChange={(newDate) => {
                // Ignore mid-edit Invalid Date objects (MUI emits one
                // while a field is being typed) so we never store NaN.
                if (newDate && Number.isNaN(newDate.getTime())) return
                setRangeEndDate(newDate)
                // Notify parent if both dates are set
                if (newDate && rangeStartDate && onDateRangeChange) {
                  onDateRangeChange(rangeStartDate, newDate)
                }
                // Auto-switch back to single date mode if cleared
                if (!newDate && !rangeStartDate) {
                  setDateRangeMode(false)
                }
              }}
              minDate={rangeStartDate || undefined}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: { xs: '100%', sm: 180 } },
                },
              }}
            />
          </>
        ) : (
          // Single Date Mode
          <DatePicker
            label={translate('pos.glucose.select_date')}
            value={selectedDate}
            onChange={(newDate) => {
              // Ignore mid-edit Invalid Date objects (MUI emits one while
              // a field is being typed) so we never store NaN.
              if (newDate && Number.isNaN(newDate.getTime())) return
              setSelectedDate(newDate)
            }}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: { xs: '100%', sm: 200 } },
                placeholder: firstMetricTimestamp
                  ? format(new Date(firstMetricTimestamp * 1000), 'MMM d, yyyy')
                  : format(new Date(), 'MMM d, yyyy'),
              },
            }}
          />
        )}
      </LocalizationProvider>

      {/* Mode Toggle Button */}
      <Button
        size="small"
        variant={dateRangeMode ? 'contained' : 'outlined'}
        onClick={() => {
          setDateRangeMode(!dateRangeMode)
          // Clear dates when switching modes
          if (!dateRangeMode) {
            setSelectedDate(null)
          } else {
            setRangeStartDate(null)
            setRangeEndDate(null)
          }
        }}
        sx={{ minWidth: 100 }}
      >
        {dateRangeMode ? translate('pos.glucose.single_date') : translate('pos.glucose.date_range')}
      </Button>

      {/* Clear Button */}
      {(selectedDate || rangeStartDate || rangeEndDate) && (
        <Button
          size="small"
          onClick={() => {
            setSelectedDate(null)
            setRangeStartDate(null)
            setRangeEndDate(null)
          }}
          variant="outlined"
        >
          {translate('ra.action.clear')}
        </Button>
      )}
    </Box>
  )
}

/**
 * The control strip above the chart: date picker(s) with single/range mode
 * toggle, the day/week/month/all presets, reset, and the activity-marker
 * visibility toggle. Pure view over the state the parent owns.
 */
const ChartToolbar = ({
  dateRangeMode,
  setDateRangeMode,
  selectedDate,
  setSelectedDate,
  rangeStartDate,
  setRangeStartDate,
  rangeEndDate,
  setRangeEndDate,
  onDateRangeChange,
  viewType,
  setViewType,
  isResetEnabled,
  onReset,
  showActivityMarkers,
  setShowActivityMarkers,
  firstMetricTimestamp,
}: {
  dateRangeMode: boolean
  setDateRangeMode: (v: boolean) => void
  selectedDate: Date | null
  setSelectedDate: (v: Date | null) => void
  rangeStartDate: Date | null
  setRangeStartDate: (v: Date | null) => void
  rangeEndDate: Date | null
  setRangeEndDate: (v: Date | null) => void
  onDateRangeChange?: (startDate: Date, endDate: Date) => void
  viewType: ViewType
  setViewType: (v: ViewType) => void
  isResetEnabled: boolean
  onReset: () => void
  showActivityMarkers: boolean
  setShowActivityMarkers: (v: boolean) => void
  /** First reading's timestamp (seconds), for the single-date picker placeholder. */
  firstMetricTimestamp?: number
}) => {
  const translate = useTranslate()
  const isCompact = useIsCompact()
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', md: 'center' },
        mb: 2,
        gap: 2,
      }}
    >
      <DateSelectionControls
        dateRangeMode={dateRangeMode}
        setDateRangeMode={setDateRangeMode}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        rangeStartDate={rangeStartDate}
        setRangeStartDate={setRangeStartDate}
        rangeEndDate={rangeEndDate}
        setRangeEndDate={setRangeEndDate}
        onDateRangeChange={onDateRangeChange}
        firstMetricTimestamp={firstMetricTimestamp}
      />

      {/* View Type Switcher - Quick Presets */}
      <ButtonGroup
        variant="outlined"
        size="small"
        fullWidth={isCompact}
        sx={{ width: { xs: '100%', md: 'auto' } }}
      >
        {VIEW_PRESETS.map((preset) => (
          <Button
            key={preset.type}
            variant={viewType === preset.type ? 'contained' : 'outlined'}
            onClick={() => {
              setViewType(preset.type)
            }}
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          >
            {translate(preset.labelKey, { _: preset.fallback })}
          </Button>
        ))}
      </ButtonGroup>

      {/* Activity Markers Toggle & Settings Icons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
        <Button size="small" variant="outlined" onClick={onReset} disabled={!isResetEnabled}>
          {translate('pos.glucose.reset_view', { _: 'Reset view' })}
        </Button>
        <Tooltip
          title={translate(
            showActivityMarkers
              ? 'pos.glucose.hide_activity_markers'
              : 'pos.glucose.show_activity_markers',
          )}
        >
          <IconButton
            onClick={() => setShowActivityMarkers(!showActivityMarkers)}
            size="small"
            sx={{
              transition: 'all 0.3s ease',
              color: showActivityMarkers ? 'primary.main' : 'text.secondary',
              '&:hover': {
                transform: 'scale(1.1)',
                backgroundColor: showActivityMarkers
                  ? 'rgba(33, 150, 243, 0.1)'
                  : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            {showActivityMarkers ? <VisibilityIcon /> : <VisibilityOffIcon />}
          </IconButton>
        </Tooltip>
        {/* Target-range gear removed — the labeled "Configure Target Range"
          button beside the "Glucose Trend" heading (PetGlucoseDetailPanel)
          opens this same dialog via the imperative handle. */}
      </Box>
    </Box>
  )
}

/** Target-range settings dialog (opened by the imperative handle / parent button). */
const TargetRangeSettingsDialog = ({
  open,
  tempLow,
  tempHigh,
  glucoseUnit,
  onChangeLow,
  onChangeHigh,
  onCancel,
  onSave,
  speciesFallback = false,
  species,
}: {
  open: boolean
  tempLow: number
  tempHigh: number
  glucoseUnit: string
  onChangeLow: (value: number) => void
  onChangeHigh: (value: number) => void
  onCancel: () => void
  onSave: () => void
  /** This pet's species has no band of its own; the values shown are DOG defaults. */
  speciesFallback?: boolean
  species?: string
}) => {
  const translate = useTranslate()
  const step = glucoseUnit === 'mg/dL' ? 1 : 0.1
  // NaN mid-edit (cleared field) renders as-is, like the previous inline form did
  const preview = (v: number) =>
    typeof v === 'number' ? v.toFixed(glucoseUnit === 'mg/dL' ? 0 : 1) : v
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{translate('pos.glucose.target_range_settings')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 300 }}>
          <TextField
            label={translate('pos.glucose.target_low')}
            type="number"
            value={tempLow}
            onChange={(e) => onChangeLow(parseFloat(e.target.value))}
            inputProps={{ step, min: 0 }}
            helperText={`${translate('pos.glucose.current_unit')}: ${glucoseUnit}`}
          />
          <TextField
            label={translate('pos.glucose.target_high')}
            type="number"
            value={tempHigh}
            onChange={(e) => onChangeHigh(parseFloat(e.target.value))}
            inputProps={{ step, min: 0 }}
            helperText={`${translate('pos.glucose.current_unit')}: ${glucoseUnit}`}
          />
          {speciesFallback && (
            // Neutral, not a status badge: this is a provenance note, not a
            // clinical alert (UI style guide — colour is clinical priority only).
            <Typography variant="caption" color="text.secondary">
              {translate('pos.glucose.species_fallback_notice', {
                species: species ?? translate('pos.common.empty_data'),
              })}
            </Typography>
          )}
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(76, 175, 80, 0.3)',
            }}
          >
            {translate('pos.glucose.target_range_preview')}: {preview(tempLow)} -{' '}
            {preview(tempHigh)} {glucoseUnit}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{translate('ra.action.cancel')}</Button>
        <Button onClick={onSave} variant="contained" color="primary">
          {translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const GlucoseTrendChart = React.memo(
  forwardRef<GlucoseTrendChartHandle, GlucoseTrendChartProps>(
    (
      {
        deviceId: propDeviceId,
        petId: propPetId,
        enableRangeSlider = false,
        startDate: propStartDate,
        endDate: propEndDate,
        selectedDateKeys = [],
        defaultView = 'day',
        onDateRangeChange,
        targetMin: propTargetMin,
        targetMax: propTargetMax,
        highMax: propHighMax,
        onReadyChange,
      },
      ref,
    ) => {
      const translate = useTranslate()
      const isCompact = useIsCompact()
      const [storeDeviceId] = useStore('selectedDeviceId')
      const deviceId = propDeviceId ?? storeDeviceId
      const [storePetId] = useStore('selectedPetId')
      const petId = propPetId ?? storePetId
      // Display unit is controlled solely by the app-bar switcher; the chart only
      // reads it. (No inline toggle here — there is one place to set the unit.)
      const { glucoseUnit } = useGlucoseUnitPreference()
      const externalDateRangeEnabled = !!(propStartDate || propEndDate)
      const defaultDateRangeMode = defaultView === 'all'
      const initialDateRangeMode = externalDateRangeEnabled || defaultDateRangeMode
      const [viewType, setViewType] = useState<ViewType>(defaultView)
      const [datePickerOpen, setDatePickerOpen] = useState(false)
      const [selectedDate, setSelectedDate] = useState<Date | null>(null)
      const [dateRangeMode, setDateRangeMode] = useState<boolean>(initialDateRangeMode)
      const [rangeStartDate, setRangeStartDate] = useState<Date | null>(propStartDate || null)
      const [rangeEndDate, setRangeEndDate] = useState<Date | null>(propEndDate || null)
      const [showActivityMarkers, setShowActivityMarkers] = useStore<boolean>(
        'showActivityMarkers',
        true,
      )

      // Target range — resolved by the ONE hook every clinical surface uses
      // (pet record → local cache → species default; the chart used to
      // re-implement the layering inline, which is how it and the hook could
      // drift). Props remain as an explicit override for callers that pass one.
      const {
        targetMin: resolvedTargetMin,
        targetMax: resolvedTargetMax,
        species: resolvedSpecies,
        usingSpeciesFallback,
        setTargetRange,
      } = usePetTargetRange(petId)
      const targetRangeLow = propTargetMin ?? resolvedTargetMin
      const targetRangeHigh = propTargetMax ?? resolvedTargetMax
      const [settingsOpen, setSettingsOpen] = useState(false)
      const [tempLow, setTempLow] = useState<number>(targetRangeLow)
      const [tempHigh, setTempHigh] = useState<number>(targetRangeHigh)
      const [chartResetVersion, setChartResetVersion] = useState(0)
      const [hasZoomOrPan, setHasZoomOrPan] = useState(false)
      const [hasSelection, setHasSelection] = useState(false)
      const [hasTransientHighlight, setHasTransientHighlight] = useState(false)
      const propStartTimestamp = propStartDate?.getTime() ?? null
      const propEndTimestamp = propEndDate?.getTime() ?? null

      // Unit comes from useGlucoseUnitPreference (write-capable) above, so
      // this chart uses the pure helpers rather than useGlucoseDisplay.
      const convertValue = (mmolValue: number) =>
        convertGlucose(mmolValue, glucoseUnit as GlucoseUnit)
      const formatValue = (mmolValue: number) =>
        formatGlucoseValue(mmolValue, glucoseUnit as GlucoseUnit)

      // Convert target range to current unit
      const lowThreshold = convertValue(targetRangeLow ?? 4.4)
      const highThreshold = convertValue(targetRangeHigh ?? 11.1)

      // The target range is stored canonically in mmol/L; the dialog edits it in
      // the active display unit and converts back on save. displayGlucoseToMmol,
      // not bare mgdlToMmol: integer mg/dL rounding makes the 2.2 mmol floor
      // read back as 2.21998, which slips past the <=2.2 out-of-range mask in
      // the hover text below (and stores a near-bound band edit inexactly).
      const toMmol = (displayValue: number) => displayGlucoseToMmol(displayValue, glucoseUnit)

      // Handler functions for settings dialog
      const handleOpenSettings = () => {
        setTempLow(convertValue(targetRangeLow ?? 4.4))
        setTempHigh(convertValue(targetRangeHigh ?? 11.1))
        setSettingsOpen(true)
      }

      // Expose openSettings() to ancestors via ref (see GlucoseTrendChartHandle).
      useImperativeHandle(ref, () => ({ openSettings: handleOpenSettings }))

      const handleSaveSettings = () => {
        if (typeof tempLow === 'number' && typeof tempHigh === 'number') {
          // Writes the local cache AND PATCHes the pet record (mg/dL) — the
          // record is what AdapetApp shows the owner, so the two must not
          // diverge (2026-08-10 decision: server field is the source of truth).
          setTargetRange(toMmol(tempLow), toMmol(tempHigh))
        }
        setSettingsOpen(false)
      }

      const handleCancelSettings = () => {
        setTempLow(convertValue(targetRangeLow ?? 4.4))
        setTempHigh(convertValue(targetRangeHigh ?? 11.1))
        setSettingsOpen(false)
      }

      const handleRelayout = (eventData: Record<string, unknown>) => {
        const relayoutKeys = Object.keys(eventData || {})
        const hasAxisChange = relayoutKeys.some(
          (key) =>
            key.includes('xaxis.range') ||
            key.includes('yaxis.range') ||
            key.includes('xaxis.autorange') ||
            key.includes('yaxis.autorange'),
        )

        if (!hasAxisChange) return

        const hasManualRange = relayoutKeys.some(
          (key) => key.includes('axis.range') && eventData[key] !== undefined,
        )
        const isAutorangeResetOnly =
          !hasManualRange &&
          relayoutKeys.length > 0 &&
          relayoutKeys.every((key) => key.includes('axis.autorange') && eventData[key] === true)

        setHasZoomOrPan(!isAutorangeResetOnly)
      }

      const isResetEnabled =
        hasZoomOrPan ||
        hasSelection ||
        hasTransientHighlight ||
        hasLocalFilterOverrides({
          viewType,
          defaultView,
          dateRangeMode,
          initialDateRangeMode,
          selectedDate,
          rangeStartDate,
          rangeEndDate,
          propStartTimestamp,
          propEndTimestamp,
        })

      const handleChartReset = () => {
        setChartResetVersion((prev) => prev + 1)
        setHasZoomOrPan(false)
        setHasSelection(false)
        setHasTransientHighlight(false)
        setViewType(defaultView)
        setSelectedDate(null)
        setDateRangeMode(initialDateRangeMode)
        setRangeStartDate(propStartDate || null)
        setRangeEndDate(propEndDate || null)
      }

      useEffect(() => {
        setDateRangeMode(initialDateRangeMode)
        setRangeStartDate((prev) => {
          const prevTs = prev?.getTime() ?? null
          if (prevTs === propStartTimestamp) return prev
          return propStartTimestamp === null ? null : new Date(propStartTimestamp)
        })
        setRangeEndDate((prev) => {
          const prevTs = prev?.getTime() ?? null
          if (prevTs === propEndTimestamp) return prev
          return propEndTimestamp === null ? null : new Date(propEndTimestamp)
        })
      }, [initialDateRangeMode, propStartTimestamp, propEndTimestamp])

      // Fetch ALL metrics for the device(s) without date limits.
      // This keeps the React Query cache key stable across view switches
      // (day/week/month/all) so data is never refetched unnecessarily.
      // Date range filtering happens client-side in defensivelyFilteredData below.
      const {
        data: metricsData = [],
        isLoading: isLoadingMetrics,
        error: errorMetrics,
        isVirtualDevice: isVirtual,
        deviceIds,
        hiddenRanges,
      } = useDeviceMetrics({
        deviceId,
        petId: petId,
        sort: { field: 'timestamp', order: 'ASC' },
        // Explicit + shared with the PDF's device-wide probe. This is
        // DELIBERATELY unwindowed (zoom/pan must reach all history), so it can
        // never share the panel's windowed key — but the two device-wide queries
        // can share each other's (P17-1). Previously this fell through to
        // getDefaultMetricsPagination() = 17280, a third distinct key.
        pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
      })

      // Activities for marker overlays on the chart. A failure here must NOT
      // hide the (valid) glucose curve — but silently rendering zero markers is
      // indistinguishable from "nothing was logged", which is clinically
      // misleading in the opposite direction from a metrics error. Read the
      // error and surface a small non-blocking indicator (see `errorEvents`
      // below) instead of hiding it.
      const { data: events = [], error: errorEvents } = useGetList<Activity>(
        'activities',
        {
          // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
          meta: { params: { depth: 0 } },
          pagination: { page: 1, perPage: 10000 },
          sort: { field: 'start_time', order: 'ASC' },
          filter: petId ? { pet: petId } : {},
        },
        { enabled: !!petId },
      )

      // Structured meal-status and insulin markers (vic/vetpo#15). Wired the
      // same way as `activities` above; a fetch failure here folds into the
      // same non-blocking indicator (see `errorEvents` usage below) rather
      // than a second failure state.
      const { data: feedingEvents = [], error: errorFeedingEvents } = useGetList<FeedingEvent>(
        'feeding-events',
        {
          // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
          meta: { params: { depth: 0 } },
          pagination: { page: 1, perPage: 10000 },
          sort: { field: 'actualTime', order: 'ASC' },
          filter: petId ? { pet: petId } : {},
        },
        { enabled: !!petId },
      )
      const { data: insulinEvents = [], error: errorInsulinEvents } = useGetList<InsulinEvent>(
        'insulin-events',
        {
          // Explicit depth (L-089): REST default 2 embeds every relation per row; only ids are read.
          meta: { params: { depth: 0 } },
          pagination: { page: 1, perPage: 10000 },
          sort: { field: 'actualTime', order: 'ASC' },
          filter: petId ? { pet: petId } : {},
        },
        { enabled: !!petId },
      )
      const structuredMarkerEvents = useMemo(
        () => [
          ...feedingEventsToMarkers(feedingEvents, translate),
          ...insulinEventsToMarkers(insulinEvents, translate),
        ],
        [feedingEvents, insulinEvents, translate],
      )

      // Memoize initialRange to prevent infinite loops
      // Dependencies are the actual state values that affect the time range calculation
      // IMPORTANT: For 'all' view, we derive the range from first/last metric timestamps,
      // so we must include them as deps — metricsData.length alone is insufficient because
      // a refetch can return the same count but different data (e.g., switching from
      // dateRangeMode to 'all' mode triggers a new API call whose response may have the
      // same length but a different date range).
      const firstMetricTs = metricsData[0]?.timestamp
      const lastMetricTs =
        metricsData.length > 0 ? metricsData[metricsData.length - 1]?.timestamp : undefined
      // biome-ignore lint/correctness/useExhaustiveDependencies: metricsData is deliberately tracked via length + first/last timestamps (see comment above) — the array reference changes on every refetch even when its contents are equivalent
      const initialRange = useMemo(() => {
        return getInitialTimeRange({
          viewType,
          selectedDateKeys,
          propStartDate,
          propEndDate,
          dateRangeMode,
          rangeStartDate,
          rangeEndDate,
          selectedDate,
          metricsData,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        viewType,
        selectedDate,
        dateRangeMode,
        rangeStartDate,
        rangeEndDate,
        propStartDate,
        propEndDate,
        selectedDateKeys,
        metricsData.length,
        firstMetricTs,
        lastMetricTs,
      ])

      const linkedSelectionRange = useMemo(() => {
        if (selectedDateKeys.length === 0) {
          return null
        }

        // Prefer explicit linked range from parent when provided (Dashboard passes this
        // from Daily TIR click selection), otherwise derive from selected date keys.
        if (propStartDate && propEndDate) {
          return {
            start: startOfDay(propStartDate),
            end: endOfDay(propEndDate),
          }
        }

        const selectedDates = selectedDateKeys
          .map((dateKey) => parseISO(dateKey))
          .filter((date) => !Number.isNaN(date.getTime()))

        if (selectedDates.length === 0) {
          return null
        }

        const minTs = Math.min(...selectedDates.map((date) => date.getTime()))
        const maxTs = Math.max(...selectedDates.map((date) => date.getTime()))
        return {
          start: startOfDay(new Date(minTs)),
          end: endOfDay(new Date(maxTs)),
        }
      }, [selectedDateKeys, propStartDate?.getTime(), propEndDate?.getTime()])

      const effectiveRange = linkedSelectionRange ?? initialRange
      const viewStartTime = effectiveRange.start
      const viewEndTime = effectiveRange.end

      // Notify parent component when the displayed date range changes
      useEffect(() => {
        if (onDateRangeChange && effectiveRange.start && effectiveRange.end) {
          onDateRangeChange(effectiveRange.start, effectiveRange.end)
        }
      }, [effectiveRange, onDateRangeChange])

      // Filter data for the selected time range (+ optional linked Daily-TIR
      // day filter). If no data lands in the view window, show an empty chart —
      // falling back to all data caused blips at chart edges.
      const defensivelyFilteredData = filterMetricsToWindow(
        metricsData,
        viewStartTime,
        viewEndTime,
        selectedDateKeys,
      )

      const filteredMetrics = prepareGlucoseChartSeries(defensivelyFilteredData)

      // (A per-period min/max/avg aggregation used to live here feeding the
      // daily Min/Max annotations; d185f2c2 removed the annotations and the
      // aggregation ran write-only for a year. Deleted 2026-08-16.)

      // Process data - for all views, show individual readings
      // The view type affects the time range and grouping visualization, not the data points
      const processedData = filteredMetrics
        .filter((m): m is typeof m & { timestamp: number } => m.timestamp !== undefined)
        .map((m) => ({
          timestamp: new Date(m.timestamp * 1000),
          glucose: convertValue(m.glucose),
        }))

      const metricsX = processedData.map((d) => d.timestamp)
      const metricsY = processedData.map((d) => d.glucose)

      // Filter events for activity markers on the chart. Legacy activities and
      // the structured meal/insulin markers are combined BEFORE filtering so
      // they share one time-window pass and one marker pipeline (vic/vetpo#15).
      const chartStartTime = effectiveRange.start.getTime()
      const chartEndTime = effectiveRange.end.getTime()

      const combinedMarkerEvents: MarkerSource[] = [...events, ...structuredMarkerEvents]
      const filteredEvents = combinedMarkerEvents.filter((e) => {
        if (!e.start_time) return false
        const timestamp =
          typeof e.start_time === 'number' ? e.start_time * 1000 : new Date(e.start_time).getTime()
        return timestamp >= chartStartTime && timestamp <= chartEndTime
      })

      // Part 4 Enhancement: Use fixed Y-axis span for smoother curves
      // Fixed range: 0 to 30 mmol/L (or equivalent in mg/dL) to show all glucose values without clipping
      const fixedAxis = getFixedGlucoseAxis(glucoseUnit)
      const yAxisMin = fixedAxis.min
      const yAxisMax = fixedAxis.max

      const { shapes, annotations } = buildChartDecorations({
        hiddenRanges,
        translate,
        metricsX,
        targetRangeLow,
        targetRangeHigh,
        lowThreshold,
        highThreshold,
        highMax: propHighMax,
        yAxisMax,
        glucoseUnit,
        convertValue,
        formatValue,
      })

      // The settings dialog renders only in the main return below (the early
      // returns for loading/error/no-data don't mount it). Report readiness so
      // the parent's "Configure Target Range" button is disabled until then.
      const chartReady =
        !isLoadingMetrics && !errorMetrics && filteredMetrics.length > 0 && metricsX.length > 0
      useEffect(() => {
        onReadyChange?.(chartReady)
      }, [chartReady, onReadyChange])

      if (isLoadingMetrics) {
        return (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}
          >
            <CircularProgress size={40} />
          </Box>
        )
      }
      if (errorMetrics) {
        return (
          <div>
            {translate('pos.glucose.error_loading_trend')}:{' '}
            {errorMetrics?.message || translate('pos.common.unknown_error')}
          </div>
        )
      }

      // Check if we have any data to display
      if (filteredMetrics.length === 0 || metricsX.length === 0) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              color: '#666',
              fontSize: '16px',
            }}
          >
            {translate('pos.glucose.no_data_available')}
          </div>
        )
      }

      const traces: any[] = [
        ...buildGlucoseLineTraces({
          metricsX,
          metricsY: metricsY as number[],
          rawWindowRows: defensivelyFilteredData,
          lowThreshold,
          highThreshold,
          // Clamp the plotted line into the visible axis band so out-of-range
          // highs (and sensor-floor lows) ride the boundary in grey instead of
          // leaving a gap.
          displayFloor: convertValue(GLUCOSE_VALID_RANGE.min),
          displayCeil: yAxisMax,
          glucoseUnit,
          toMmol,
          translate,
        }),
      ]

      // Min/Max labels removed per user request

      // Add activity markers if enabled
      if (showActivityMarkers && filteredEvents.length > 0) {
        const markersTrace = buildActivityMarkersTrace({
          filteredEvents,
          processedData,
          defaultY: (yAxisMin + yAxisMax) / 2, // mid-axis when no glucose data is near
          translate,
        })
        if (markersTrace) traces.push(markersTrace)
      }

      const { plotKey, uirevision } = buildPlotRevisionKeys({
        deviceId,
        viewType,
        selectedDate,
        rangeStartDate,
        rangeEndDate,
        glucoseUnit,
        chartResetVersion,
      })

      return (
        <div>
          {/* Virtual Device Indicator */}
          {isVirtual && deviceIds.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={translate('pos.glucose.showing_devices', { count: deviceIds.length })}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}

          {/* Activity markers load-failure indicator (S3): the glucose curve
            above is still valid and rendered — this is a non-blocking caption
            so a failed insulin/food/exercise fetch doesn't silently read as
            "nothing was logged in this window". Covers legacy activities and
            the structured feeding-events/insulin-events sources alike. */}
          {(errorEvents || errorFeedingEvents || errorInsulinEvents) && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={translate('pos.glucose.activity_markers_load_error')}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: STATUS_TONES.attention.borderColor,
                  bgcolor: STATUS_TONES.attention.bgcolor,
                  color: STATUS_TONES.attention.color,
                }}
              />
            </Box>
          )}

          {/* View Type Switcher, Date Picker, and Settings */}
          {/* Stacks vertically below md so the date controls and preset buttons
            don't collide on phones; identical row layout at md+. */}
          <ChartToolbar
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            rangeStartDate={rangeStartDate}
            setRangeStartDate={setRangeStartDate}
            rangeEndDate={rangeEndDate}
            setRangeEndDate={setRangeEndDate}
            onDateRangeChange={onDateRangeChange}
            viewType={viewType}
            setViewType={(view: ViewType) => {
              setViewType(view)
              // The vet's own range switch — not the reset-to-default path.
              trackNow({ type: 'chart_view', detail: view, pet: Number(petId) })
            }}
            isResetEnabled={isResetEnabled}
            onReset={handleChartReset}
            showActivityMarkers={showActivityMarkers}
            setShowActivityMarkers={setShowActivityMarkers}
            firstMetricTimestamp={metricsData[0]?.timestamp}
          />

          {/* Target Range Settings Dialog */}
          <TargetRangeSettingsDialog
            open={settingsOpen}
            tempLow={tempLow}
            tempHigh={tempHigh}
            glucoseUnit={glucoseUnit}
            onChangeLow={setTempLow}
            onChangeHigh={setTempHigh}
            onCancel={handleCancelSettings}
            onSave={handleSaveSettings}
            speciesFallback={usingSpeciesFallback && propTargetMin == null && propTargetMax == null}
            species={resolvedSpecies}
          />

          {/* Main Glucose Trend Chart */}
          {/* Explicit height on the wrapper prevents Plotly's useResizeHandler
            from measuring a collapsed parent on the initial paint and locking
            in a near-zero chart height until the user refreshes. */}
          {/* touchAction 'pan-y' (mobile/tablet only) lets the browser keep
            vertical page-scroll while handing horizontal-drag + pinch gestures
            to Plotly, so pinch-to-zoom works on the chart instead of triggering
            browser page-zoom. Desktop is left at the default (undefined) — no
            change to mouse behavior. */}
          <div
            data-testid="glucose-trend-chart"
            style={{ height: 300, touchAction: isCompact ? 'pan-y' : undefined }}
          >
            <Plot
              key={plotKey}
              data={traces}
              layout={buildTrendChartLayout({
                translate,
                glucoseUnit,
                effectiveRange,
                viewType,
                enableRangeSlider,
                yAxisMin,
                yAxisMax,
                fixedAxis,
                shapes,
                annotations,
                uirevision,
              })}
              onRelayout={handleRelayout}
              onSelected={() => setHasSelection(true)}
              onDeselect={() => setHasSelection(false)}
              onHover={() => setHasTransientHighlight(true)}
              onUnhover={() => setHasTransientHighlight(false)}
              useResizeHandler
              style={{ width: '100%', height: '100%' }}
              config={{
                displayModeBar: true,
                responsive: true,
                doubleClick: 'reset',
                showTips: false,
                scrollZoom: true,
                staticPlot: false,
                modeBarButtonsToRemove: ['select2d', 'lasso2d'],
                modeBarButtonsToAdd: ['pan2d', 'zoom2d', 'zoomIn2d', 'zoomOut2d', 'resetScale2d'],
              }}
            />
          </div>
        </div>
      )
    },
  ),
)

GlucoseTrendChart.displayName = 'GlucoseTrendChart'
